import React from 'react';
import _ from 'underscore';

import * as OnboardingActions from 'actions/OnboardingActions';
import {EntityStoreUser, Referral} from 'constants/EntityStoreConstants';
import notifications from 'notifications';
import EmailInvite from 'start/EmailInvite';
import PublicReferralLink from 'start/PublicReferralLink';
import EntityStore from 'stores/EntityStore';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MakeFieldsRequired} from 'util/Types';

import {AcademicInviteAvatars} from './InviteAvatars';
import {AcademicReferralsDescription} from './ReferralsDescription';
import ReferralsTable from './ReferralsTable';

function mapEntityStoreToProps(entityStore: typeof EntityStore): ConnectedProps {
  return {
    user: entityStore.currentUser(),
  };
}

interface ConnectedProps {
  user: MakeFieldsRequired<EntityStoreUser, 'referrals'>;
}

interface State {
  showReferralTable: boolean;
}

function getSortedReferrals(referrals: Array<Referral>): Array<Referral> {
  return _.clone(referrals).sort((invite1: Referral, invite2: Referral) => {
    // Sort by [-user?, -createdAt, email]
    if ((invite1.user != null) !== (invite2.user != null)) {
      if (invite1.user != null) {
        return -1;
      } else {
        return 1;
      }
    } else if (invite1.createdAt !== invite2.createdAt) {
      if (invite1.createdAt < invite2.createdAt) {
        return 1;
      } else {
        return -1;
      }
    } else if (invite1.email < invite2.email) {
      return -1;
    } else {
      return 1;
    }
  });
}

export class InvitePage extends React.Component<ConnectedProps, State> {
  constructor(props: ConnectedProps) {
    super(props);
    this.state = {
      showReferralTable: props.user.referrals.length > 0,
    };
  }

  toggleShowReferralTable = (): void => {
    this.setState({showReferralTable: !this.state.showReferralTable});
  };

  inviteMembers = (emails: Array<string>, recaptcha: string): void => {
    if (emails.length === 0) {
      notifications.error('Please specify an email address to invite.');
      return;
    }

    OnboardingActions.inviteUsers({emails, recaptcha});
    this.setState({showReferralTable: true});
  };

  render(): JSX.Element {
    const referrals = getSortedReferrals(this.props.user.referrals);
    const numReferralsAccepted = referrals.filter(({user}) => user != null).length;
    return (
      <div className="invitePage">
        <Row>
          <Col md={4}>
            <AcademicInviteAvatars numReferralsAccepted={numReferralsAccepted} />
          </Col>
          <Col md={8} className="u-spacing-bottom">
            <div className="col-centered">
              <AcademicReferralsDescription />
              <EmailInvite onInvite={this.inviteMembers} />
              <div className="u-flex-display u-spacing-top">
                <PublicReferralLink email={this.props.user.email} />
              </div>
            </div>
          </Col>
        </Row>
        <div className="u-small-spacing-top">
          <span className="faded">
            <a onClick={this.toggleShowReferralTable}>
              {`Referrals ${referrals.length}`}
              <BenchlingIcon
                icon={this.state.showReferralTable ? 'caret-down' : 'caret-up'}
                className="u-tiny-spacing-left faded invitePage-showTable"
              />
            </a>
          </span>
        </div>
        {this.state.showReferralTable && <ReferralsTable user={this.props.user} referrals={referrals} />}
      </div>
    );
  }
}

export default EntityStore.connect(mapEntityStoreToProps)(InvitePage);
